import { format, parse } from "date-fns";

export class DateUtils {
    /**
     * Formata uma data para exibição no formato brasileiro (DD/MM/AAAA)
     * @param dateValue Data no formato ISO ou string
     * @returns Data formatada ou string vazia
     */
    static formatDisplayDate(dateValue: string | null | undefined): string {
        if (!dateValue) return "";
        const parsed = parse(dateValue, "yyyy-MM-dd", new Date()); // mantém local, não UTC
        return this.parseDateToPtBr(parsed);
    }

    /**
     * Converte uma data no formato YYYY-MM-DD para Date sem problemas de timezone
     * @param dateString Data no formato YYYY-MM-DD
     * @returns Date object local ou null se inválida
     */
    static parseLocalDate(dateString?: string | null): Date | null {
        if (!dateString) return null;

        // Verifica se está no formato YYYY-MM-DD
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateString)) return null;

        // Divide a string e cria Date local (sem conversão de timezone)
        const [year, month, day] = dateString.split("-").map(Number);
        return new Date(year, month - 1, day); // month é 0-indexed
    }

    /**
     * Converte string de data (com ou sem hora) para Date local.
     * Suporta: YYYY-MM-DD, YYYY-MM-DD HH:mm:ss e YYYY-MM-DDTHH:mm:ss.
     * @param dateValue Data em string ou Date
     * @returns Date local ou null se inválida
     */
    static parseDateTimeToLocalDate(dateValue?: string | Date | null): Date | null {
        if (!dateValue) return null;

        if (dateValue instanceof Date) {
            return Number.isNaN(dateValue.getTime()) ? null : dateValue;
        }

        const trimmedDateValue = dateValue.trim();
        if (!trimmedDateValue) return null;

        const dateTimeRegex = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/;
        const match = trimmedDateValue.match(dateTimeRegex);

        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        const hour = Number(match[4] ?? 0);
        const minute = Number(match[5] ?? 0);
        const second = Number(match[6] ?? 0);

        const parsedDate = new Date(year, month - 1, day, hour, minute, second);

        if (
            parsedDate.getFullYear() !== year ||
            parsedDate.getMonth() !== month - 1 ||
            parsedDate.getDate() !== day ||
            parsedDate.getHours() !== hour ||
            parsedDate.getMinutes() !== minute ||
            parsedDate.getSeconds() !== second
        ) {
            return null;
        }

        return parsedDate;
    }

    /**
     * Converte uma data para o formato brasileiro (DD/MM/AAAA)
     * @param date Data a ser formatada
     * @param includeTime Se true, inclui o horário na formatação
     * @returns Data formatada no formato brasileiro (DD/MM/AAAA) ou (DD/MM/AAAA HH:MM)
     */
    static parseDateToPtBr(date: Date | null | undefined, includeTime: boolean = false): string {
        if (!date) return "";

        return `${date.toLocaleDateString("pt-BR")}${includeTime ? " " + date.toLocaleTimeString("pt-BR") : ""}`;
    }

    /**
     * Formata uma data para o formato YYYY-MM-DD
     * @param date Data a ser formatada
     * @returns Data formatada no formato YYYY-MM-DD
     */
    static formatDateToYYYYMMDD(date: Date | null | undefined): string {
        if (!date) return "";
        return format(date, "yyyy-MM-dd");
    }

    /**
     * Valida se uma string representa um horário válido
     * @param timeString Horário no formato "HH:mm" ou "HH:mm:ss"
     * @returns true se o horário é válido, false caso contrário
     */
    static isValidTime(timeString: string | null | undefined): boolean {
        if (!timeString || timeString.trim() === "") {
            return false;
        }

        // Regex para validar formato HH:mm ou HH:mm:ss
        const timeRegex = /^([0-1][0-9]|2[0-3]):([0-5][0-9])(:([0-5][0-9]))?$/;

        if (!timeRegex.test(timeString)) {
            return false;
        }

        // Extrai horas, minutos e segundos
        const parts = timeString.split(":");
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parts[2] ? parseInt(parts[2], 10) : 0;

        // Valida ranges
        return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59;
    }

    /**
     * Compara dois horários e verifica se o primeiro é menor que o segundo
     * @param startTime Horário inicial no formato "HH:mm" ou "HH:mm:ss"
     * @param endTime Horário final no formato "HH:mm" ou "HH:mm:ss"
     * @returns true se startTime < endTime, false caso contrário
     */
    static isTimeRangeValid(startTime: string | null | undefined, endTime: string | null | undefined): boolean {
        if (!this.isValidTime(startTime) || !this.isValidTime(endTime)) {
            return false;
        }

        // Normaliza os horários para comparação (adiciona segundos se não existir)
        const normalizeTime = (time: string): string => {
            const parts = time.split(":");
            return parts.length === 2 ? `${time}:00` : time;
        };

        const normalizedStart = normalizeTime(startTime!);
        const normalizedEnd = normalizeTime(endTime!);

        return normalizedStart < normalizedEnd;
    }

    /**
     * Converte uma string de horário para um formato específico
     * @param timeString Horário no formato "HH:mm" ou "HH:mm:ss"
     * @param outputFormat Formato de saída desejado (ex: "HH:mm")
     * @returns Horário formatado ou string vazia se houver erro ou hora inválida
     */
    static parseTime(timeString: string | null | undefined, outputFormat: string): string {
        try {
            if (!timeString || timeString.trim() === "") {
                return "";
            }

            // Valida a hora antes de fazer parse
            if (!this.isValidTime(timeString)) {
                return "";
            }

            const inputFormat = timeString.split(":").length === 3 ? "HH:mm:ss" : "HH:mm";

            const date = parse(timeString, inputFormat, new Date());
            return format(date, outputFormat);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            return "";
        }
    }
}
